import { BigSwitch, ReactIcons } from "@kalyzee/kalyzee-ui";
import { useTriggerable } from "@kalyzee/kast-web-sdk/web-components";
import { ROLE } from "@kalyzee/kast-web-sdk/http";
import clsx from "clsx";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BannerButton } from "../../../../components/common/BannerButton";
import { CreateMeetingPopup } from "../../../../components/common/CreateMeetingPopup";
import { MasterUI } from "../../../../components/common/MasterUI";
import { MediaConfigPopup } from "../../../../components/common/MediaConfigPopup";
import { MediaGlobalControls, MediaSourcePicker } from "../../../../components/common/MediaSourcePicker";
import { PageMasterMode, usePageContextRef } from "../../../../components/navigation/PageContext";
import { AnimatedBackground } from "../../../../components/utils/AnimatedBackground";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { isRoleGreaterThanOrEqual } from "../../../../helpers/roles";
import { useMeetingSources } from "../../../../hooks/meeting";
import { VodPlayerContentPage } from "../vod/player";
import { EventsAgenda } from "./EventsAgenda";
import styles from "./list.module.css";
import { VodsAgenda } from "./VodsAgenda";

type AgendaView = "events" | "vods";

const SWITCH_OPTIONS: { value: AgendaView; label: string }[] = [
  { value: "events", label: "Activités" },
  { value: "vods", label: "Replays" },
];

export const MeetingListStudentPage = () => {
  const pageContextRef = usePageContextRef<SessionMode.MASTER>();
  const sources = useMeetingSources();

  const isMobile = useAppStoreSelector(state => state.isMobile);
  const userRole = useAppStoreSelector(state => state.user?.role);
  const canCreateMeeting = userRole ? isRoleGreaterThanOrEqual(userRole, ROLE.ORGANIZER) : false;

  const hasVisibleSources = useMemo(() => sources.some(s => s.visible), [sources]);

  const logoContainerRef = useRef<HTMLImageElement>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [mediaConfigOpen, setMediaConfigOpen] = useState(false);

  const rawVodId = pageContextRef.current?.searchParams?.get("vodId") ?? null;
  const vodId = rawVodId && rawVodId !== "_" ? rawVodId : undefined;
  const view: AgendaView = rawVodId !== null ? "vods" : "events";

  useTriggerable(logoContainerRef, { clickCount: 3 }, () => pageContextRef.current?.openSettingsPopup());

  const onChangeView = useCallback((value: AgendaView) => {
    pageContextRef.current?.goTo(
      PageMasterMode.LIST,
      undefined,
      { replace: true, queries: { view: undefined, vodId: value === "vods" ? "_" : undefined } },
    );
  }, [pageContextRef]);

  const navigate = useNavigate();
  const onCloseVod = useCallback(() => navigate(-1), [navigate]);

  return (
    <MasterUI onBack={vodId ? onCloseVod : undefined}>
      {vodId ? (
        <VodPlayerContentPage id={vodId} />
      ) : (
        <AnimatedBackground contentClassName={styles.backgroundHome}>
          <div className={styles.mainHomeLogged}>
            <div className={styles.mainHomeLoggedContent}>
              <div className={clsx(styles.layout, !isMobile && !hasVisibleSources && styles.layoutNoMedia)}>
                {!isMobile && hasVisibleSources && (
                  <div className={styles.mediaColumn}>
                    <MediaSourcePicker
                      sources={sources}
                      className={styles.mediaSelector}
                      // We can ignore gesture errors
                      // here because this component is only used to configure the sources
                      // it will be mounted again when joining the meeting
                      // and there wont be gesture errors anymore
                      ignoreGestureError
                    />
                    <MediaGlobalControls sources={sources} className={styles.globalControls} />
                  </div>
                )}

                <div className={styles.switchColumn}>
                  <BigSwitch
                    className={styles.bigSwitch}
                    options={SWITCH_OPTIONS}
                    value={view}
                    onChange={onChangeView}
                  />
                </div>

                <div className={styles.eventsColumn}>
                  {view === "events" ? <EventsAgenda /> : <VodsAgenda />}
                </div>

                <div className={styles.actionButtons}>
                  {isMobile && (
                    <BannerButton
                      icon={<ReactIcons.Settings />}
                      title="Configurer mes médias"
                      subtitle="Choisissez caméra et micro avant de rejoindre"
                      background="linear-gradient(135deg, var(--color-meet-purple-2) 0%, var(--color-meet-purple-1) 100%)"
                      onClick={() => setMediaConfigOpen(true)}
                    />
                  )}
                  {canCreateMeeting && (
                    <BannerButton
                      icon={<ReactIcons.Timer />}
                      title="Créer un meeting instantané"
                      subtitle="Démarrez un nouveau cours en quelques secondes"
                      background="var(--color-element-glass-bg)"
                      style={{
                        color: "var(--color-agenda-title)",
                        ["--banner-icon-circle-bg" as any]: "var(--color-element-banner-icon-circle-bg)",
                      }}
                      onClick={() => setCreateOpen(true)}
                    />
                  )}
                  {!isMobile && !hasVisibleSources && (
                    <MediaGlobalControls sources={sources} className={styles.globalControls} />
                  )}
                </div>
              </div>

              <CreateMeetingPopup
                visible={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(meeting) => {
                  setCreateOpen(false);
                  // Pass `openShare` via react-router state so the meeting
                  // page knows to auto-open the share popup on arrival.
                  pageContextRef.current?.goTo(
                    PageMasterMode.MEETING_DASHBOARD,
                    { id: meeting._id },
                    { state: { openShare: true } },
                  );
                }}
              />
              <MediaConfigPopup
                visible={mediaConfigOpen}
                onClose={() => setMediaConfigOpen(false)}
                sources={sources}
              />
            </div>
          </div>
        </AnimatedBackground>
      )}
    </MasterUI>
  );
};

